import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Handles Ajax cart operations for custom product cards.
 */
class CustomAjaxCart {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (event) => {
      // Open Cart Drawer on cart icon click
      const cartIconBtn = event.target.closest('#cart-icon-bubble, .header-actions__action action__cart');
      if (cartIconBtn) {
        event.preventDefault();
        this.openCartDrawer();
        return;
      }

      const cartBtn = event.target.closest('.bestseller-cart-btn');
      if (!cartBtn) return;

      const form = cartBtn.closest('form');
      if (!form) return;

      event.preventDefault();
      this.addToCart(form, cartBtn);
    });
  }

  async addToCart(form, button) {
    if (button.classList.contains('loading')) return;

    button.classList.add('loading');
    const originalContent = button.innerHTML;
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';

    const formData = new FormData(form);
    
    // Add sections to update (cart drawer sections)
    const sectionsToUpdate = this.getSectionsToUpdate();
    if (sectionsToUpdate.length > 0) {
      formData.append('sections', sectionsToUpdate.join(','));
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData
      });

      const data = await response.json();

      if (data.status) {
        // Error handling
        console.error('Cart Error:', data.description || data.message);
        alert(data.description || data.message);
      } else {
        // Success
        let newCartItemCount = 0;
        
        // Try to find the new item count from the morphed sections
        if (data.sections) {
          for (const sectionId in data.sections) {
            const html = data.sections[sectionId];
            if (html.includes('ref="cartItemCount"')) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const countEl = doc.querySelector('[ref="cartItemCount"]');
              if (countEl) {
                newCartItemCount = parseInt(countEl.textContent.trim(), 10);
                break;
              }
            }
          }
        }

        // Dispatch CartAddEvent to trigger drawer and update cart icons
        // We pass itemCount and set source != 'product-form-component' 
        // because we are passing the TOTAL count derived from sections.
        document.dispatchEvent(new CartAddEvent(data, null, {
          source: 'custom-ajax-cart',
          variantId: data.variant_id.toString(),
          sections: data.sections,
          itemCount: newCartItemCount
        }));

        // Explicitly open the cart drawer
        this.openCartDrawer();
      }
    } catch (error) {
      console.error('Fetch Error:', error);
    } finally {
      button.classList.remove('loading');
      button.innerHTML = originalContent;
    }
  }

  async openCartDrawer() {
    console.log('Attempting to open cart drawer...');
    
    try {
      // 1. Wait for the component to be defined for sure
      await customElements.whenDefined('cart-drawer-component');
      
      // 2. Find the drawer by tag name or ID
      let drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer-component');
      
      if (!drawer) {
        console.warn('cart-drawer-component not found by selector. searching by data attribute...');
        drawer = document.querySelector('[data-testid="cart-drawer-dialog"]')?.closest('cart-drawer-component');
      }

      if (drawer) {
        console.log('Drawer element found:', drawer);
        
        // 3. Try to call the specific open methods
        if (typeof drawer.open === 'function') {
          console.log('Successfully called drawer.open()');
          drawer.open();
          return;
        } 
        
        if (typeof drawer.showDialog === 'function') {
          console.log('Successfully called drawer.showDialog()');
          drawer.showDialog();
          return;
        }

        // 4. Fallback: Trigger a click on any element that is supposed to open it
        console.warn('Drawer found but no internal methods found. Trying trigger click fallback...');
        const triggers = [
          document.querySelector('[data-testid="cart-drawer-trigger"]'),
          document.getElementById('cart-icon-bubble'),
          document.querySelector('button[on\\:click*="#cart-drawer/open"]')
        ];

        for (const trigger of triggers) {
          if (trigger) {
            console.log('Clicking fallback trigger:', trigger);
            trigger.click();
            return;
          }
        }
      } else {
        console.error('CRITICAL: Cart drawer component NOT found in DOM.');
      }
    } catch (e) {
      console.error('Error in openCartDrawer:', e);
    }
  }

  getSectionsToUpdate() {
    const sectionIds = [];
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }
}

// Initialize
new CustomAjaxCart();
